import React, { useMemo } from 'react'
import { InstallationAccessRightsContext } from './InstallationAccessRightsContext'
import { useTypedSelector } from 'src/State/RootReducer'
import { UserLevel } from 'src/State/User/UserModel'
import { useAccountLinkContext } from 'src/Libs/installation-rights-management/provider/AccountLinksContext'
import { isFlowTheme } from 'src/Libs/utils'
import { getHasInstallationWritingRights } from '../utils/get-has-installation-writing-rights'

interface Props {
  companyId?: number
  userLevel?: UserLevel
  userName?: string
  children?: React.ReactNode
  ssoId?: string
}

/**
 * This provider gives the information on if a user has only read or also write rights for a given installation.
 *
 * This is especially relevant for users who are invited to an installation (user, installer, or partner).
 * Those users are linked to the installation via the accountLinks, which includes the information about
 * their access rights.
 *
 * For higher user levels, such as service, this linkage is irrelevant. Instead, those levels can generally access all installations with writing rights.
 *
 */
const InstallationAccessRightsProvider: React.FC<Props> = ({ companyId, userLevel = 'USER', userName, children, ssoId }) => {
  const { accountLinks, isLoadingAccountLinks } = useAccountLinkContext()
  const activeInstallation = useTypedSelector(state => state.storage.activeInstallation)
  const userId = useTypedSelector(state => state.auth.userId)
  // Because the accountIDs from flow dont match with the userId, the users 'ssoId' needs to be used here
  const themedUserId = isFlowTheme ? ssoId : userId

  const hasInstallationWritingRights =
    isLoadingAccountLinks || getHasInstallationWritingRights(userLevel, accountLinks, themedUserId, userName, companyId, activeInstallation)

  const contextValue = useMemo(
    () => ({
      hasInstallationWritingRights,
    }),
    [hasInstallationWritingRights]
  )

  return <InstallationAccessRightsContext.Provider value={contextValue}>{children}</InstallationAccessRightsContext.Provider>
}

export default InstallationAccessRightsProvider
